import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '../../icons';
import { ChevronDownIcon, ChevronUpIcon } from '../uiIcons';
import './SettingsRow.css';

interface SettingsRowBase {
  label: string;
  description?: string;
  icon: ReactNode;
  className?: string;
  id?: string;
}

export interface SettingsRowLinkProps extends SettingsRowBase {
  to: string;
  onOpen?: never;
  expanded?: never;
  controls?: never;
}

export interface SettingsRowActionProps extends SettingsRowBase {
  onOpen: () => void;
  to?: never;
  /** When defined the row is a disclosure button with aria-expanded. */
  expanded?: boolean;
  controls?: string;
}

export type SettingsRowProps = SettingsRowLinkProps | SettingsRowActionProps;

/**
 * 60px translucent pill row: stroked icon circle, label, optional
 * description and a trailing chevron. A link for navigation, a button with
 * aria-expanded for in-place expansion.
 */
const SettingsRow: React.FC<SettingsRowProps> = (props) => {
  const { label, description, icon, className, id } = props;
  const isDisclosure = props.to === undefined && props.expanded !== undefined;
  const classes = `luma-settings-row${className ? ` ${className}` : ''}`;

  const body = (
    <>
      <span className="luma-settings-row__icon" aria-hidden="true">{icon}</span>
      <span className="luma-settings-row__text">
        <span className="luma-settings-row__label">{label}</span>
        {description && <span className="luma-settings-row__description">{description}</span>}
      </span>
      <span className="luma-settings-row__chevron" aria-hidden="true">
        {isDisclosure ? (props.expanded ? <ChevronUpIcon size={22} /> : <ChevronDownIcon size={22} />) : <ArrowRightIcon size={22} />}
      </span>
    </>
  );

  if (props.to !== undefined) {
    return (
      <Link id={id} className={classes} to={props.to}>
        {body}
      </Link>
    );
  }

  return (
    <button
      id={id}
      type="button"
      className={classes}
      onClick={props.onOpen}
      aria-expanded={isDisclosure ? props.expanded : undefined}
      aria-controls={props.controls}
    >
      {body}
    </button>
  );
};

export default SettingsRow;
